import posthog from 'posthog-js';

/**
 * Runs `fn` once the page has loaded and the main thread is idle. Session
 * replay (rrweb, ~500 ms of script on a mid-range phone) and surveys are
 * started here so they stay off the startup path Lighthouse measures.
 */
const whenPageSettled = (fn: () => void): void => {
    const DELAY_AFTER_LOAD_MS = 3000;
    const IDLE_TIMEOUT_MS = 2000;
    const schedule = () =>
        window.setTimeout(() => {
            if ('requestIdleCallback' in window) {
                window.requestIdleCallback(fn, { timeout: IDLE_TIMEOUT_MS });
            } else {
                fn();
            }
        }, DELAY_AFTER_LOAD_MS);
    if (document.readyState === 'complete') schedule();
    else window.addEventListener('load', schedule, { once: true });
};

class PosthogAnalyticsEvents {
    /** Mirrors Firebase `tracking_feature` / `tracking_feature_QA` after `tracking.init()`. */
    private optIn = false;

    /**
     * Initialises the PostHog SDK when Remote Config allows it. Safe to call
     * multiple times — posthog-js ignores duplicate init() for the same key.
     */
    public setup(allow: boolean): void {
        if (typeof window === 'undefined') return;
        this.optIn = allow === true;
        if (!this.optIn) return;

        const loaded = !!(posthog as { __loaded?: boolean }).__loaded;
        if (loaded) return;

        posthog.init(process.env.NEXT_PUBLIC_POSTHOG_API_KEY as string, {
            api_host: process.env.NEXT_PUBLIC_POSTHOG_HOST as string,
            defaults: '2026-01-30',
            person_profiles: 'always',
            autocapture: false,
            capture_pageview: false,
            capture_pageleave: false,
            // Both are turned on in `loaded` once the page has settled.
            disable_session_recording: true,
            disable_surveys: true,
            session_recording: {
                maskAllInputs: true,
                maskTextSelector: 'input, textarea',

                // Custom network scrubbing to protect Hotdesk PII & Location vectors
                maskCapturedNetworkRequestFn: (request) => {
                    // 1. Redact sensitive query parameters from the request URL string
                    if (request.name) {
                        request.name = request.name.replace(
                            /([?&](token|auth|email|first_name|last_name|phone|lat|lng)=)[^&]+/g,
                            '$1[REDACTED]'
                        );
                    }

                    // 2. Define standard PostHog blocks + our app-specific variables
                    const sensitiveKeys = [
                        // PostHog defaults (Must explicitly declare since custom fn overrides native scrubbing)
                        'password',
                        'secret',
                        'passwd',
                        'api_key',
                        'apikey',
                        'auth',
                        'credentials',
                        'mysql_pwd',
                        'privatekey',
                        'private_key',
                        'token',
                        // Application PII, Contact, & Spatial Profiles
                        'email',
                        'phone_number',
                        'jwt',
                        'first_name',
                        'last_name',
                        'name',
                        'location',
                        'coordinate',
                        'location_pin',
                        'formatted_address',
                        'lat',
                        'lng',
                        'token',
                        'email',
                        'first_name',
                        'last_name',
                        'phone',
                        'lat',
                        'lng',
                        'password',
                        'secret',
                        'passwd',
                        'api_key',
                        'apikey',
                        'auth',
                        'credentials',
                        'mysql_pwd',
                        'privatekey',
                        'private_key',
                        'token',
                        'email',
                        'phone_number',
                        'first_name',
                        'last_name',
                        'name',
                        'location',
                        'coordinate',
                        'location_pin',
                        'formatted_address',
                        'lat',
                        'lng'
                    ];

                    const scrubPayload = (
                        body: string | null | undefined
                    ): string | null | undefined => {
                        if (body == null || body === '') return body;
                        let parsedBody = body;
                        try {
                            sensitiveKeys.forEach((key) => {
                                // Regex looks for "key": "value", "key": 12.34, "key": true, etc. inside JSON strings
                                const regex = new RegExp(
                                    `"${key}"\\s*:\\s*("[^"]*"|[^,\\s}]+)`,
                                    'gi'
                                );
                                parsedBody = parsedBody.replace(
                                    regex,
                                    `"${key}": "[REDACTED]"`
                                );
                            });
                        } catch (e) {
                            // Fallback to avoid breaking telemetry if parsing errors emerge
                            return e ? '[UNPARSEABLE_BODY_REDACTED]' : body;
                        }
                        return parsedBody;
                    };

                    // Scrub request data going out and response data coming back
                    request.requestBody = scrubPayload(request.requestBody);
                    request.responseBody = scrubPayload(request.responseBody);

                    return request;
                }
            },
            loaded: (posthogInstance) => {
                posthogInstance.capture('Web App Opened', {
                    landing_page: window.location.pathname,
                    full_url: window.location.href
                });
                whenPageSettled(() => {
                    // No override: the project's sampling / linked-flag
                    // settings still decide whether this session is recorded.
                    posthogInstance.startSessionRecording();
                    // With surveys disabled while remote config arrived, the
                    // SDK never stored whether the project has any, so force
                    // the load; it then fetches and shows active surveys.
                    posthogInstance.set_config({
                        disable_surveys: false,
                        advanced_enable_surveys: true
                    });
                });
            }
        });
    }

    private get ready(): boolean {
        return (
            typeof window !== 'undefined' &&
            this.optIn &&
            !!(posthog as { __loaded?: boolean }).__loaded
        );
    }

    /**
     * @param person Person properties for PostHog (`posthog.identify`).
     */
    public setUserId(
        id?: string,
        person?: Record<string, string | number | boolean>
    ): void {
        if (!this.ready) return;
        if (id) {
            posthog.identify(id, person);
        } else {
            posthog.reset();
        }
    }

    /** Updates person properties without changing distinct id (Flutter `addToPosthog`). */
    public setPersonProperties(
        properties: Record<string, string | number | boolean>
    ): void {
        if (!this.ready) return;
        const id = posthog.get_distinct_id?.();
        if (id) {
            posthog.identify(id, properties);
        }
    }

    public actionTaken(
        eventName: string,
        params: Record<string, string | number | boolean>
    ): void {
        if (!this.ready) return;
        posthog.capture(eventName, params);
    }

    public onScreenChange(
        screenType: string,
        screenUrl: string,
        params: Record<string, string | number | boolean>
    ): void {
        if (!this.ready) return;
        posthog.capture('$pageview', {
            $current_url: screenUrl,
            screen_type: screenType,
            ...params
        });
    }

    public onLoginSkipped(): void {
        if (!this.ready) return;
        posthog.capture('login_skipped');
    }

    public onSignupSkipped(): void {
        if (!this.ready) return;
        posthog.capture('signup_skipped');
    }

    public onLoginBackClicked(): void {
        if (!this.ready) return;
        posthog.capture('login_back_clicked');
    }

    public onSignupBackClicked(): void {
        if (!this.ready) return;
        posthog.capture('signup_back_clicked');
    }

    public onVerifyPhoneLater(
        eventName: string,
        params: Record<string, string | number | boolean>
    ): void {
        if (!this.ready) return;
        posthog.capture(eventName, params);
    }
}

export const posthogEvents = new PosthogAnalyticsEvents();
